import React, { useMemo } from 'react';
import { isTeamEligibleForDivision } from '@sulis-liiga/common';
import type { Division, Category, Gender } from '@sulis-liiga/common';

// ─── Ladder grouping ────────────────────────────────────────────────────────

export interface LadderOption {
  groupId: string;
  baseName: string;
  category: string;
  tierCount: number;
}

/**
 * Group divisions by divisionGroupId to form "ladders" — each ladder may have
 * multiple tiers but is a single enroll target.
 */
export function groupByLadder(divisions: Division[]): Map<string, Division[]> {
  const map = new Map<string, Division[]>();
  for (const div of divisions) {
    const key = div.divisionGroupId ?? div.id;
    const list = map.get(key) ?? [];
    list.push(div);
    map.set(key, list);
  }
  return map;
}

/**
 * The ladders a team (given its players' genders) may enroll in, minus any the
 * team is already enrolled in. Eligibility is size + the division's gender rule
 * (see `isTeamEligibleForDivision`) — a singles player only sees singles ladders,
 * a male+female pair sees a no-restriction men's-doubles ladder and any mixed
 * ladder, etc.
 */
export function eligibleLadders(
  divisions: Division[],
  genders: Gender[],
  excludeGroupIds: string[] = [],
): LadderOption[] {
  const excluded = new Set(excludeGroupIds);
  const options: LadderOption[] = [];
  groupByLadder(divisions).forEach((tiers, groupId) => {
    if (excluded.has(groupId)) return;
    const rep = tiers[0]; // representative tier for eligibility + labelling
    if (isTeamEligibleForDivision(genders, rep.category as Category, rep.genderRestriction)) {
      options.push({
        groupId,
        baseName: rep.baseName ?? rep.name,
        category: rep.category,
        tierCount: tiers.length,
      });
    }
  });
  return options.sort((a, b) => a.baseName.localeCompare(b.baseName));
}

// ─── Component ──────────────────────────────────────────────────────────────

interface EnrollDivisionPickerProps {
  divisions: Division[];
  /** The (prospective) team's player genders — one per player. */
  genders: Gender[];
  /** Selected ladder groupIds (controlled). */
  selected: string[];
  onChange: (groupIds: string[]) => void;
  /** Ladders the team is already enrolled in — hidden to avoid duplicate enrolls. */
  excludeGroupIds?: string[];
  loading?: boolean;
}

/**
 * A controlled multi-select of eligible division ladders. Used both in the
 * Create Team wizard (mandatory division step, pre-team) and on the team detail
 * page (re-enroll an existing active team into further ladders).
 */
const EnrollDivisionPicker: React.FC<EnrollDivisionPickerProps> = ({
  divisions,
  genders,
  selected,
  onChange,
  excludeGroupIds = [],
  loading = false,
}) => {
  const options = useMemo(
    () => eligibleLadders(divisions, genders, excludeGroupIds),
    [divisions, genders, excludeGroupIds],
  );

  const toggle = (groupId: string) => {
    if (selected.includes(groupId)) {
      onChange(selected.filter(id => id !== groupId));
    } else {
      onChange([...selected, groupId]);
    }
  };

  if (loading) {
    return <p className="mt-2 text-sm text-gray-400">Loading divisions…</p>;
  }

  if (divisions.length === 0) {
    return (
      <p className="mt-2 text-sm text-gray-500">
        No divisions available for this league's current season yet.
      </p>
    );
  }

  if (options.length === 0) {
    return (
      <p className="mt-2 text-sm text-gray-500">
        No eligible division ladders found for your team's composition. Contact a league
        administrator for assistance.
      </p>
    );
  }

  return (
    <div className="mt-3 space-y-2" role="group" aria-label="Choose one or more division ladders">
      {options.map(ladder => {
        const isSelected = selected.includes(ladder.groupId);
        return (
          <label
            key={ladder.groupId}
            className={`border rounded-md p-3 flex items-center gap-3 cursor-pointer transition-colors ${
              isSelected
                ? 'border-2 border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-primary-300'
            }`}
          >
            <input
              type="checkbox"
              name="ladder"
              value={ladder.groupId}
              checked={isSelected}
              onChange={() => toggle(ladder.groupId)}
              className="text-primary-600 focus:ring-primary-500 rounded"
              aria-label={ladder.baseName}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{ladder.baseName}</div>
              <div className="text-xs text-gray-400">
                {ladder.category} &middot; {ladder.tierCount}{' '}
                {ladder.tierCount === 1 ? 'tier' : 'tiers'}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
};

export default EnrollDivisionPicker;
